import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { ClipboardList } from "lucide-react";
import { useMemo, useState } from "react";

import {
  AdminListPage,
  AdminListTable,
  contentColumns,
  DescriptionField,
  ROW_LINK_CLASS,
  TitleSlugFields,
  useAdminListCreateState,
} from "~/components/cih/admin-list-page";
import {
  challengeQueryKeys,
  challengesQueryOptions,
  createChallengeFn,
  type ChallengeListRow,
} from "~/lib/functions/cih/challenges";
import {
  listSearchSchema,
  listSortDefaults,
  type SortColumn,
  sortListRows,
} from "~/lib/shared/admin-list-sort";
import { nextSortSearch } from "~/lib/shared/sort";

const CHALLENGE_SORTS = ["title", "created"] as const;
type ChallengeSort = (typeof CHALLENGE_SORTS)[number];

const CHALLENGE_SORT_DEFAULTS = listSortDefaults<ChallengeSort>("title", [
  "created",
]);

const CHALLENGE_SORT_COLUMNS: Record<
  ChallengeSort,
  SortColumn<ChallengeListRow>
> = {
  title: { kind: "text", value: (row) => row.title },
  created: { kind: "date", value: (row) => row.createdAt },
};

export const Route = createFileRoute("/admin/cih/challenges/")({
  staticData: { title: "Challenges" },
  validateSearch: listSearchSchema(CHALLENGE_SORTS),
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(challengesQueryOptions());
    return {};
  },
  component: ChallengesPage,
});

interface CreateFormState {
  title: string;
  slug: string;
  description: string;
}

const EMPTY_FORM: CreateFormState = { title: "", slug: "", description: "" };

const CHALLENGE_COLUMNS: ColumnDef<ChallengeListRow>[] = [
  {
    id: "title",
    header: "Title",
    cell: ({ row }) => (
      <Link
        to="/admin/cih/challenges/$challengeId"
        params={{ challengeId: row.original.id }}
        className={ROW_LINK_CLASS}
      >
        {row.original.title}
      </Link>
    ),
  },
  ...contentColumns<ChallengeListRow>({
    publicPath: (slug) => `/coaching-for/${slug}`,
    pendingDraft: (row, className) =>
      row.pendingDraftId && (
        <Link
          to="/admin/cih/challenges/$challengeId"
          params={{ challengeId: row.pendingDraftId }}
          className={className}
        >
          Draft pending
        </Link>
      ),
  }),
];

function ChallengesPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate({ from: Route.fullPath });
  const urlSearch = Route.useSearch();
  const sort = urlSearch.sort ?? CHALLENGE_SORT_DEFAULTS.sort;
  const direction = urlSearch.dir ?? CHALLENGE_SORT_DEFAULTS.direction(sort);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const { create, form, setFormField } = useAdminListCreateState(EMPTY_FORM);

  const { data: challenges, isLoading } = useQuery(
    challengesQueryOptions(statusFilter || undefined),
  );

  const createMutation = useMutation({
    mutationFn: createChallengeFn,
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: challengeQueryKeys.all });
      create.finish("Challenge created.");
      navigate({
        to: "/admin/cih/challenges/$challengeId",
        params: { challengeId: created.id },
      });
    },
  });

  const handleSubmit = (): void => {
    if (!form.title.trim() || !form.description.trim()) return;
    createMutation.mutate({
      data: {
        title: form.title.trim(),
        slug: form.slug.trim() || undefined,
        description: form.description.trim(),
      },
    });
  };

  const shownChallenges = useMemo((): ChallengeListRow[] => {
    if (!challenges) return [];
    const q = search.toLowerCase();
    const matches = q
      ? challenges.filter(
          (c) =>
            c.title.toLowerCase().includes(q) ||
            (c.slug ?? "").toLowerCase().includes(q),
        )
      : challenges;
    return sortListRows(
      matches,
      sort,
      direction,
      CHALLENGE_SORT_COLUMNS,
      (c) => c.title,
    );
  }, [challenges, search, sort, direction]);

  const changeSort = (column: ChallengeSort): void => {
    navigate({
      search: (previous) => ({
        ...previous,
        ...nextSortSearch({ sort, direction }, column, CHALLENGE_SORT_DEFAULTS),
      }),
    });
  };

  const createFields = (
    <>
      <TitleSlugFields
        create={create}
        idPrefix="ch"
        titleLabel="Title"
        title={form.title}
        slug={form.slug}
        titlePlaceholder="e.g., Work-Life Balance"
        slugPlaceholder="e.g., work-life-balance"
        onTitleChange={(val) => setFormField("title", val)}
        onSlugChange={(val) => setFormField("slug", val)}
      />
      <DescriptionField
        id="ch-description"
        value={form.description}
        placeholder="Brief description of this challenge..."
        onChange={(val) => setFormField("description", val)}
      />
    </>
  );

  return (
    <AdminListPage
      title="Challenges"
      description="L1 pillar pages — coaching-for/[slug]"
      itemName="Challenge"
      create={create}
      createFields={createFields}
      onCreateSubmit={handleSubmit}
      canCreate={Boolean(form.title.trim() && form.description.trim())}
      isCreating={createMutation.isPending}
      createError={createMutation.error}
      statusFilter={{ value: statusFilter, onChange: setStatusFilter }}
      search={{
        value: search,
        onChange: setSearch,
        placeholder: "Search challenges...",
      }}
      isLoading={isLoading}
    >
      <AdminListTable
        rows={shownChallenges}
        columns={CHALLENGE_COLUMNS}
        sorts={CHALLENGE_SORTS}
        sort={sort}
        direction={direction}
        onSortChange={changeSort}
        empty={{
          isFiltered: Boolean(search),
          icon: ClipboardList,
          itemName: "Challenge",
          title: "No challenges yet",
          description: "Create a challenge to get started.",
          onCreate: create.open,
        }}
      />
    </AdminListPage>
  );
}
